<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Monthly Invoice Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyInvoiceValueFilter"
              @reset="resetInvoiceValueFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingInvoiceValueFilter.startDate"
                  :endDate="editingInvoiceValueFilter.endDate"
                  @updateStartDate="editingInvoiceValueFilter.startDate = $event"
                  @updateEndDate="editingInvoiceValueFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedInvoiceValueFilter.startDate | formatDate}} - {{appliedInvoiceValueFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Invoice Value" highlight>
              {{formatCurrency(invoiceSummaryData.invoiceTotalValue)}}
              <template v-slot:info>Over {{invoiceSummaryData.invoiceTotalCount}} invoice{{displayCountPlural(invoiceSummaryData.invoiceTotalCount)}} during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Monthly Value">
              {{formatCurrency(invoiceSummaryData.averageInvoiceValue)}}
              <template v-slot:info>Over {{invoiceSummaryData.invoiceTotalCount}} invoice{{displayCountPlural(invoiceSummaryData.invoiceTotalCount)}} during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow v-if="invoiceSummaryData.largestInvoiceInfo">
          <div class="col">
            <AnalyticsDashboardValue title="Largest Invoice">
              {{formatCurrency(invoiceSummaryData.largestInvoiceInfo.totalPrice)}}
              <template v-slot:info>Invoice <LinkInvoice :id="invoiceSummaryData.largestInvoiceInfo.id" :name="invoiceSummaryData.largestInvoiceInfo.sequenceNumber"></LinkInvoice> for {{invoiceSummaryData.largestInvoiceInfo.contact.name}} on {{formatDate(invoiceSummaryData.largestInvoiceInfo.dateInvoiceCreated)}}</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Monthly Invoice Value"
          :series="invoiceDailyValueData.series"
          :xAxis="invoiceDailyValueData.xAxis"
          :yAxis="invoiceDailyValueData.yAxis"
          :legend="invoiceDailyValueData.legend"
          :options="invoiceDailyValueData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'InvoiceMonthlyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      invoiceSummaryData: {
        invoiceTotalValue: 0,
        invoiceTotalCount: 0,
        averageInvoiceValue: 0,
        largestInvoiceInfo: null
      },
      invoiceDailyValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultInvoiceValueFilter: {    
        startDate: moment().local().subtract(7, 'years').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingInvoiceValueFilter: {},
      appliedInvoiceValueFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.editingInvoiceValueFilter = _.clone(this.defaultInvoiceValueFilter)
    this.appliedInvoiceValueFilter = _.clone(this.defaultInvoiceValueFilter)

    this.requestChartData()

    this.subscribeEndpoint('/accounts/invoices', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/accounts/invoices')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyInvoiceValueFilter() {
      this.appliedInvoiceValueFilter = _.clone(this.editingInvoiceValueFilter)

      this.requestChartData()
    },
    resetInvoiceValueFilter() {
      this.editingInvoiceValueFilter = _.clone(this.defaultInvoiceValueFilter)
      this.appliedInvoiceValueFilter = _.clone(this.defaultInvoiceValueFilter)

      this.requestChartData()
    },
    requestChartData() {
      var invoiceValueFilter = {
        type: 'monthly',
        startDate: this.appliedInvoiceValueFilter.startDate,
        endDate: this.appliedInvoiceValueFilter.endDate,
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      Promise.all([
        this.$apiService.analyticsService.getInvoiceValueChartData(invoiceValueFilter),
        this.$apiService.analyticsService.getInvoiceValueSummary(invoiceValueFilter)
      ])
      .then(results => {
        let chartData = results[0].data
        let invoiceSummaryData = results[1].data

        let monthList = []
        let seriesList = []
        let yearList = []
        for(let i=0; i<12; i++)
          monthList.push(moment().month(i).format('MMM'))

        // Format scattered data into chart data
        if(chartData.length > 0) {
          let firstYear = chartData[0].year
          let lastYear = moment().local().year()

          for(let year=firstYear; year<=lastYear; year++) {
            yearList.push(`${year}`)

            let yearData = chartData.filter(invoiceValueInfo => invoiceValueInfo.year == year)
            let yearValueSeries = monthList.map((m, monthIndex) => {
              let monthInfo = yearData.find(invoiceValueInfo => invoiceValueInfo.month == monthIndex + 1)   // Note: Month is one based (ie, 1-12)
              return monthInfo ? monthInfo.totalInvoiceValue : 0
            })

            seriesList.push({
              name: `${year}`,
              type: 'bar',
              data: yearValueSeries,
              markLine: {
                label: {
                  formatter: (param) => {
                    return this.formatCurrency(param.value);
                  }
                },
                data: [
                  {
                    type: 'average',
                    name: 'Avg',
                  }
                ]
              },
              tooltip: {
                valueFormatter: (value) => {
                  return this.formatCurrency(value);
                }
              }
            })
          }
        }

        this.invoiceDailyValueData.legend = {
          data: yearList
        }
        this.invoiceDailyValueData.xAxis = {
          type: 'category',
          data: monthList
        }
        this.invoiceDailyValueData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return this.formatCurrencySuffix(value, 1);
              }
            }
          }
        ]

        this.invoiceDailyValueData.series = seriesList

        // Summary info
        this.invoiceSummaryData.averageInvoiceValue = 0
        if(chartData.length > 0) {
          this.invoiceSummaryData.averageInvoiceValue = chartData.reduce((curValue, invoiceInfo) => curValue + invoiceInfo.totalInvoiceValue, 0) / chartData.length
        }

        this.invoiceSummaryData.invoiceTotalValue = invoiceSummaryData.totalInvoicedAmount
        this.invoiceSummaryData.invoiceTotalCount = invoiceSummaryData.totalInvoicedCount
        this.invoiceSummaryData.largestInvoiceInfo = invoiceSummaryData.largestInvoice        
      })
    },
  }
}
</script>